/**
 * Using an accessible dialog or "modal" window. From Radix UI
 * https://www.radix-ui.com/docs/primitives/components/dialog
 */
import React from 'react';
import {
    Dialog,
    DialogPortal,
    DialogOverlay,
    DialogContent
} from '@radix-ui/react-dialog';
import MediaQuery from 'react-responsive';

import { BREAKPOINT_SIZES, BREAKPOINTS } from 'tradera/constants/breakpoints';
import styles from './dialog-modal.module.scss';
import classNames from 'classnames';

const mobileOnlyProps = { maxWidth: BREAKPOINT_SIZES[BREAKPOINTS.SM].max };
const desktopOnlyProps = { minWidth: BREAKPOINT_SIZES[BREAKPOINTS.MD].min };
const allDeviceProps = { minWidth: BREAKPOINT_SIZES[BREAKPOINTS.XS].min };

export type DialogModalProps = {
    children: React.ReactNode;
    className?: string;
    desktop?: boolean;
    dismissOnOverlayClick?: boolean;
    mediaQuery?: {
        minWidth?: number;
        maxWidth?: number;
    };
    mobile?: boolean;
    mobileFullscreen?: boolean;
    onDismiss?: () => void;
    style?: React.CSSProperties;
    overlayClassNames?: string;
};

const DialogModal = ({
    children,
    className,
    desktop,
    dismissOnOverlayClick = true,
    mediaQuery,
    mobile,
    mobileFullscreen = true,
    onDismiss,
    style,
    overlayClassNames
}: DialogModalProps) => {
    const getDefaultMediaProps = () => {
        if (mobile && desktop) {
            return allDeviceProps;
        }
        if (mobile) {
            return mobileOnlyProps;
        }
        if (desktop) {
            return desktopOnlyProps;
        }
        return allDeviceProps;
    };

    let mediaQueryProps;
    if (!mediaQuery) {
        mediaQueryProps = getDefaultMediaProps();
    } else {
        mediaQueryProps = mediaQuery;
    }

    return (
        <MediaQuery {...mediaQueryProps}>
            <Dialog
                defaultOpen
                onOpenChange={(open: boolean) => {
                    if (!open && onDismiss) {
                        onDismiss();
                    }
                }}>
                <DialogPortal forceMount>
                    <DialogOverlay
                        className={classNames(
                            styles.overlay,
                            overlayClassNames,
                            mobileFullscreen
                                ? styles.mobileFullscreen
                                : styles.mobileAlert
                        )}
                        onClick={dismissOnOverlayClick ? onDismiss : () => {}}>
                        <DialogContent
                            data-testid="dialog-modal"
                            className={classNames(styles.content, className)}
                            style={style}
                            onClick={(event) => event.stopPropagation()}>
                            {children}
                        </DialogContent>
                    </DialogOverlay>
                </DialogPortal>
            </Dialog>
        </MediaQuery>
    );
};

export default DialogModal;
